import { Skeleton } from 'antd';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useRef } from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg } from '../assets/icons';
import AnswerSvg from '../assets/icons/AnswerSvg';
import ArrowRightSvg from '../assets/icons/ArrowRightSvg';
import { MagicBackground } from '../assets/icons/MagicBackground';
import { MagicWandSvg } from '../assets/icons/MagicWandSvg';
import theme from '../assets/theme';
import { LicenseCoarseLevels, LicenseFineLevels } from '../hooks/useAccessBadge';
import { useBreakPoint } from '../hooks/useBreakPoint';
import useGames from '../hooks/useGames';
import { useIsMobile } from '../hooks/useIsMobile';
import { useMetrics } from '../hooks/useMetrics';
import usePaywallPrompt from '../hooks/usePaywallPrompt';
import useRecentTemplates from '../hooks/useRecentTemplates';
import useRoutePrefetch from '../hooks/useRoutePrefetch';
import useRouterMatch from '../hooks/useRouterMatch';
import { getTitle } from '../utils/templateUtils';
import { Button } from './_atoms/Button';
import { IconWrapper } from './_atoms/IconWrapper';
import { PresentationCard } from './_molecules/PresentationCard';
import CountrySchoolSelectionPopup from './CountrySchoolSelectionPopup';
import NewAppLayout from './layouts/NewAppLayout';
import TrialWelcomeMessagePopup from './TrialWelcomeMessagePopup';

const DiscoveryRowContainer = styled.div`
  margin-bottom: 30px;

  @media (max-width: 800px) {
    margin-bottom: 30px;
  }
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  text-transform: uppercase;
  padding-left: 30px;

  @media (max-width: 800px) {
    padding-left: 20px;
    margin-bottom: 0px;
  }

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 2}px;
  }
`;

export const ResponsiveGeneratorsGridContainer = styled.div<{ $isCentered?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;

  @media (max-width: 4096px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1440px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 950px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 800px) {
    // 800 we go into mobile screen mode
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }

  ${({ $isCentered }) =>
    $isCentered &&
    css`
      max-width: 500px;
      margin: 0 auto;
      @media (max-width: 4096px) {
        grid-template-columns: 1fr 1fr 1fr;
      }
      @media (max-width: 700px) {
        grid-template-columns: 1fr 1fr;
      }
    `}
`;

const StyledArrowButton = styled(Button)`
  padding: 0px;
  svg {
    height: 28px;
    width: 28px;
  }
`;

const GeneratorsLeftArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 40px;
  left: 20px;
  top: 0;
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
`;

const GeneratorsRightArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 40px;
  right: 20px;
  top: 0;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
`;

const ScrollMainContainer = styled.div`
  position: relative;
  width: 100%;
  height: 210px;
  padding: 0px 20px;
  @media (max-width: 800px) {
    height: 150px;
  }
`;

const HorisontalScrollContainer = styled.ul`
  width: 100%;
  height: 210px;
  overflow-x: auto;
  overflow-y: hidden;

  display: flex;
  gap: 10px;
  padding: 10px 0px 10px 0px; // padding bottom to contain scroll bar

  // Hide the horisontal scrollbars
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }

  .ant-skeleton,
  .ant-skeleton-button {
    height: 100%;
    aspect-ratio: 1.28;
    border-radius: 5px;
    min-width: unset;
    width: unset;
  }

  @media (max-width: 800px) {
    height: 150px;
  }
`;

const LessonsSlidingGrid = ({ title }: { title: string }) => {
  const { templates, isFetchingTemplates } = useRecentTemplates({ limit: 10 });
  const { tenantId } = useRouterMatch();
  const [hoveredTemplate, setHoveredTemplate] = useState<string | undefined>();
  const metrics = useMetrics();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);
    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [templates]);

  return (
    <DiscoveryRowContainer>
      {templates && templates.length >= 1 && (
        <>
          <Title $fontSize={16}>{title}</Title>
          <ScrollMainContainer>
            {showLeftArrow && (
              <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowLeftSvg />}
                    notext={true}
                    onClick={() => {
                      handleLeftArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsLeftArrowContainer>
            )}
            <HorisontalScrollContainer ref={listRef}>
              {isFetchingTemplates && templates === undefined && (
                <>
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                </>
              )}
              {templates.map(template => {
                if (
                  template?.content?.slides &&
                  template.content.slides.length > 0 &&
                  template.content.slides[0]
                ) {
                  return (
                    <PresentationWrapper>
                      <Link href={`/${tenantId}/lessons/${template.id}`}>
                        <a
                          style={{ height: '100%', width: '100%' }}
                          onMouseEnter={() => setHoveredTemplate(template.id)}
                          onMouseLeave={() => setHoveredTemplate(undefined)}
                        >
                          <PresentationCard
                            isHovering={hoveredTemplate === template.id}
                            slides={template.content.slides}
                            subTitle={`Edited:
                        ${dayjs(template.modifiedAt).format('MMMM DD, YYYY - hh:mma')}`}
                            isGenerator={!!template.aiContent?.prompts?.length}
                            title={
                              template.title
                                ? template.title
                                : getTitle({
                                    title: template.title,
                                    slides:
                                      template.type === 'template'
                                        ? template.content.slides
                                        : null,
                                    maxLength: 100,
                                  })
                            }
                          />
                        </a>
                      </Link>
                      {hoveredTemplate === template.id && (
                        <Link href={`/${tenantId}/lessons/${template.id}/edit`}>
                          <a
                            style={{ height: '100%', width: '100%' }}
                            onMouseEnter={() => setHoveredTemplate(template.id)}
                            onMouseLeave={() => setHoveredTemplate(undefined)}
                          >
                            <EditButton
                              onClick={() => {
                                metrics.logEvent(
                                  'AuthenticatedHome.EditRecentLesson.Clicked',
                                  {
                                    templateId: template.id,
                                    tenantId,
                                  },
                                );
                              }}
                              notext
                              icon={<AnswerSvg />}
                            ></EditButton>
                          </a>
                        </Link>
                      )}
                    </PresentationWrapper>
                  );
                }
              })}
            </HorisontalScrollContainer>
            {showRightArrow && templates && templates.length > 2 && (
              <GeneratorsRightArrowContainer>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowRightSvg />}
                    notext={true}
                    onClick={() => {
                      handleRightArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsRightArrowContainer>
            )}
          </ScrollMainContainer>
        </>
      )}
    </DiscoveryRowContainer>
  );
};

const PresentationWrapper = styled.div`
  height: 210px;
  width: 243px;
  max-width: 243px;
  min-width: 243px;
  position: relative;

  @media (max-width: 800px) {
    height: 150px;
    width: 166px;
    max-width: 166px;
    min-width: 166px;
  }
`;

const EditButton = styled(Button)`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  padding: 2px 5px;

  background: #00000099;
  border-radius: 5px;
  color: ${theme.colors.white};
  font-size: 12px;
  height: 30px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const ResultsSlidingGrid = ({ title }: { title: string }) => {
  const { games, isFetching } = useGames();
  const { tenantId } = useRouterMatch();

  const [hoveredGame, setHoveredGame] = useState<string | undefined>();

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [games]);

  return (
    <DiscoveryRowContainer>
      {games && games.length >= 1 && (
        <>
          <Title $fontSize={16}>{title}</Title>
          <ScrollMainContainer>
            {showLeftArrow && (
              <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowLeftSvg />}
                    notext={true}
                    onClick={() => {
                      handleLeftArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsLeftArrowContainer>
            )}
            <HorisontalScrollContainer ref={listRef}>
              {isFetching && (
                <>
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                </>
              )}
              {games.map(game => {
                if (game?.slides && game.slides.length > 0 && game.slides[0]) {
                  return (
                    <PresentationWrapper>
                      <Link href={`/${tenantId}/reports/${game.id}`}>
                        <a
                          style={{ height: '100%', width: '100%' }}
                          onMouseEnter={() => setHoveredGame(game.id)}
                          onMouseLeave={() => setHoveredGame(undefined)}
                        >
                          <PresentationCard
                            isHovering={game.id === hoveredGame}
                            slides={game.slides}
                            subTitle={`Played: 
                            ${dayjs(game.lastPlayedAt).format('MMMM DD, YYYY - hh:mma')}`}
                            title={
                              game.title
                                ? game.title
                                : getTitle({
                                    title: game.title,
                                    slides: game.slides || null,
                                    maxLength: 100,
                                  })
                            }
                          />
                        </a>
                      </Link>
                    </PresentationWrapper>
                  );
                }
              })}
            </HorisontalScrollContainer>
            {showRightArrow && games && games.length > 2 && (
              <GeneratorsRightArrowContainer>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowRightSvg />}
                    notext={true}
                    onClick={() => {
                      handleRightArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsRightArrowContainer>
            )}
          </ScrollMainContainer>
        </>
      )}
    </DiscoveryRowContainer>
  );
};

const AuthenticatedHomeV2 = () => {
  // Prefetch a single generator page
  useRoutePrefetch(`/generators/[generatorName]/g/[generatorId]`);
  // Prefetch a single lesson designer page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`);
  // Prefetch a single lesson page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]`);
  // Prefetch a single lesson result page
  useRoutePrefetch(`/[tenantId]/reports/[gameId]`);

  const isDesktop = useBreakPoint(800);
  const { userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const { tenantId, router } = useRouterMatch();
  const ismobile = useIsMobile();

  const [showSchoolSelector, setShowSchoolSelector] = useState(false);
  useEffect(() => {
    if (!userAccessBadge) return;
    setShowSchoolSelector(
      userAccessBadge.license.coarseLevel !== LicenseCoarseLevels.enterprise &&
        !userAccessBadge.organization?.country,
    );
  }, [userAccessBadge]);

  const [showTrialWelcomeMessage, setShowTrialWelcomeMessage] = useState(false);
  useEffect(() => {
    if (!userAccessBadge) return;
    const trialWelcomeMessageShown = !!localStorage.getItem('trialWelcomeMessageShown');
    if (
      userAccessBadge.license.fineLevel === LicenseFineLevels.trial &&
      !trialWelcomeMessageShown
    ) {
      setShowTrialWelcomeMessage(true);
    }
  }, [userAccessBadge]);

  const [scrollOffset, setScrollOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrollOffset(offset);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <NewAppLayout allowUnAuthenticated={true} scrollOffset={scrollOffset}>
      <PositionedContainer $isDesktop={isDesktop}>
        <TopSectionContainer>
          <TopSection>
            <BackgroundContainer>
              <MagicBackground />
            </BackgroundContainer>
            <TopSectionTitle>How will you spark curiosity today?</TopSectionTitle>
            <GetLessonButton
              styles={
                ismobile
                  ? {
                      width: 'fit-content',
                    }
                  : {}
              }
              onClick={() => router.push(`/${tenantId}/create`)}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 10,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconWrapper height="25px" width="25px">
                  <MagicWandSvg />
                </IconWrapper>

                <span>Get Lesson</span>
              </div>
            </GetLessonButton>
          </TopSection>
        </TopSectionContainer>

        <GridSection>
          <LessonsSlidingGrid title={'Recent lessons'} />
          <ResultsSlidingGrid title={'Recent reports'} />
        </GridSection>
        {showSchoolSelector && <CountrySchoolSelectionPopup />}
        {showTrialWelcomeMessage && (
          <TrialWelcomeMessagePopup userAccessBadge={userAccessBadge} duration={10} />
        )}
      </PositionedContainer>
    </NewAppLayout>
  );
};

const PositionedContainer = styled.div<{ $isDesktop: boolean }>`
  position: absolute;
  left: ${({ $isDesktop }) => ($isDesktop ? '240px' : '0px')};
  top: 60px;
  min-height: calc(100% - 60px);
  width: ${({ $isDesktop }) => ($isDesktop ? 'calc(100% - 240px)' : '100%')};

  ${({ $isDesktop }) =>
    !$isDesktop &&
    css`
      top: 0px;
      padding: 0px;
      padding-bottom: 60px;
    `}
`;

const TopSection = styled.div`
  position: relative;
  min-height: 30vh;
  padding: 20px;
  background: linear-gradient(0deg, #539980 0%, #61b195 100%);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  justify-content: center;
  align-items: center;
  @media (max-width: 767px) {
    padding-top: 60px;
    border-radius: 0px;
    margin-bottom: 20px;
  }
`;

const TopSectionContainer = styled.div`
  padding: 20px;

  overflow: hidden;
  @media (max-width: 767px) {
    margin-top: 0px;
    padding: 0px;
    border-radius: 0px;
  }
`;

const TopSectionTitle = styled.div`
  color: #fff;
  text-align: center;
  font-family: Nunito;
  font-size: 36px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  @media (max-width: 767px) {
    font-size: 24px;
  }
  @media (min-width: 768px) and (max-width: 900px) {
    font-size: 26px;
  }
`;

const GridSection = styled.div`
  position: relative;
  width: 100%;
`;

const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  svg {
    height: 98vh; // 99 and 100 adds a scroll...
    width: 100vw;
  }
`;

const GetLessonButton = styled(Button)`
  width: fit-content;
  min-height: 60px;
  min-width: 200px;
  font-size: 28px;
  font-weight: 700;
  padding: 19px 30px;
  background-color: #f99746 !important;
  color: white !important;
  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark} !important;
  }

  @media (max-width: 767px) {
    font-size: 22px;
    min-height: 40px;
    min-width: 160px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    font-size: 24px;
  }
`;

export default AuthenticatedHomeV2;
